import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

interface Contract {
  id: string;
  title: string;
  version: string;
  updated_at: string;
  content: string;
  contract_type: string;
}

export default function KvkkPage() {
  const { t, i18n } = useTranslation();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKvkkContract();
  }, []);

  useEffect(() => {
    const tc = i18n.getFixedT(i18n.language, 'common');
    document.title = tc('seo_kvkk_title');
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', tc('seo_kvkk_desc'));
    setMeta('keywords', 'KVKK, kişisel verilerin korunması, aydınlatma metni, veri gizliliği, 6698 sayılı kanun');
    setMeta('robots', 'index, follow');
    setMeta('og:title', tc('seo_kvkk_title'), true);
    setMeta('og:description', tc('seo_kvkk_desc'), true);
    setMeta('og:url', `${SITE_URL}/kvkk-aydinlatma-metni`, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/c661d4abcd085d04eff2e20676e53d11.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=KVKK%20personal%20data%20protection%20law%20Turkey%20legal%20compliance%20shield%20document%20clean%20professional%20minimal%20design%20teal%20gray%20tones&width=1200&height=630&seq=og-kvkk-v1&orientation=landscape');

    const scriptId = 'schema-kvkk';
    let script = document.getElementById(scriptId);
    if (!script) { script = document.createElement('script'); script.id = scriptId; (script as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: tc('seo_kvkk_title'),
      description: tc('seo_kvkk_desc'),
      url: `${SITE_URL}/kvkk-aydinlatma-metni`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'KVKK Aydınlatma Metni', item: `${SITE_URL}/kvkk-aydinlatma-metni` },
        ],
      },
    });
    return () => { document.getElementById(scriptId)?.remove(); };
  }, [i18n.language]);

  const fetchKvkkContract = async () => {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('contract_type', 'kvkk_disclosure')
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setContract(data);
      }
    } catch (error) {
      console.error('KVKK sözleşmesi yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const lastUpdated = contract?.updated_at
    ? new Date(contract.updated_at).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '15 Ocak 2025';
  const version = contract?.version || 'v1.0';
  const title = contract?.title || t('kvkk_title');
  const content = contract?.content || '';

  const kvkkRights = [
    {
      icon: 'ri-search-eye-line',
      text: 'Kişisel verilerinizin işlenip işlenmediğini öğrenme',
    },
    {
      icon: 'ri-file-info-line',
      text: 'İşlenmişse buna ilişkin bilgi talep etme',
    },
    {
      icon: 'ri-focus-3-line',
      text: 'İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme',
    },
    {
      icon: 'ri-group-line',
      text: 'Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme',
    },
    {
      icon: 'ri-edit-2-line',
      text: 'Eksik veya yanlış işlenmişse düzeltilmesini isteme',
    },
    {
      icon: 'ri-delete-bin-line',
      text: 'KVKK\'nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini isteme',
    },
    {
      icon: 'ri-notification-line',
      text: 'Düzeltme ve silme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme',
    },
    {
      icon: 'ri-shield-cross-line',
      text: 'İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      text: 'Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white font-['Outfit']">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]" style={{ paddingTop: 'var(--navbar-height)' }}>
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">{t('legal_loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height)' }}>
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 left-10 w-40 h-40 border border-white rounded-full"></div>
            <div className="absolute bottom-10 right-20 w-60 h-60 border border-white rounded-full"></div>
            <div className="absolute top-20 right-40 w-20 h-20 border border-white rounded-full"></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <i className="ri-shield-check-line text-teal-400"></i>
              <span className="text-sm text-white/80">{t('kvkk_law_badge')}</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">{title}</h1>
            <p className="text-base text-white/70">
              {t('legal_last_updated')}: {lastUpdated} &middot; {t('legal_version')}: {version}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Info banner */}
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 mb-10">
            <div className="flex items-start space-x-3">
              <i className="ri-shield-check-line text-teal-500 text-lg mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-sm text-teal-800 leading-relaxed font-medium mb-1">
                  {t('kvkk_info_title')}
                </p>
                <p className="text-sm text-teal-700 leading-relaxed">
                  {t('kvkk_info_text')}
                </p>
              </div>
            </div>
          </div>

          {/* Quick summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl mb-3">
                <i className="ri-database-2-line text-rose-500 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_data_controller')}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{t('kvkk_data_controller_desc')}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl mb-3">
                <i className="ri-lock-line text-amber-500 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_data_security')}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{t('kvkk_data_security_desc')}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl mb-3">
                <i className="ri-mail-send-line text-teal-500 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_application_right')}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{t('kvkk_application_right_desc')}</p>
            </div>
          </div>

          {/* Content */}
          {content ? (
            <div
              className="prose prose-sm max-w-none text-gray-700 leading-relaxed mb-14"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div className="text-center py-12 mb-14">
              <i className="ri-file-list-line text-gray-300 text-5xl mb-4"></i>
              <p className="text-gray-500">{t('legal_loading')}</p>
            </div>
          )}

          {/* KVKK Rights section */}
          <div className="mb-14">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl flex-shrink-0">
                <i className="ri-shield-user-line text-rose-500 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{t('kvkk_rights_title')}</h2>
                <p className="text-xs text-gray-500">{t('kvkk_rights_subtitle')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {kvkkRights.map((right, idx) => (
                <div
                  key={idx}
                  className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-rose-200 hover:bg-rose-50/30 transition-all"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg flex-shrink-0 shadow-sm">
                    <i className={`${right.icon} text-rose-400 text-sm`}></i>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{right.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Application methods */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-14 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <i className="ri-send-plane-line text-teal-500"></i>
              <span>{t('kvkk_application_methods')}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl mb-3">
                  <i className="ri-mail-line text-teal-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_email_method')}</h3>
                <p className="text-xs text-gray-500 mb-2">{t('kvkk_email_method_desc')}</p>
                <a href="mailto:kvkk@saglik-platform.com" className="text-xs text-teal-600 font-medium hover:underline cursor-pointer" rel="nofollow">
                  kvkk@saglik-platform.com
                </a>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl mb-3">
                  <i className="ri-mail-check-line text-amber-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_kep_method')}</h3>
                <p className="text-xs text-gray-500 mb-2">{t('kvkk_kep_method_desc')}</p>
                <span className="text-xs text-amber-600 font-medium">tupbebek@hs01.kep.tr</span>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl mb-3">
                  <i className="ri-file-paper-2-line text-rose-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('kvkk_written_method')}</h3>
                <p className="text-xs text-gray-500 mb-2">{t('kvkk_written_method_desc')}</p>
                <span className="text-xs text-rose-600 font-medium">{t('kvkk_written_method_note')}</span>
              </div>
            </div>
            <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start space-x-2">
                <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                <p
                  className="text-xs text-amber-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: t('kvkk_response_note') }}
                />
              </div>
            </div>
          </div>

          {/* Related pages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14">
            <Link
              to="/gizlilik-politikasi"
              className="flex items-center space-x-4 p-5 bg-white border border-gray-200 rounded-xl hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                <i className="ri-shield-line text-teal-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-teal-700 transition-colors">{t('kvkk_related_privacy')}</h3>
                <p className="text-xs text-gray-500">{t('kvkk_related_privacy_desc')}</p>
              </div>
              <i className="ri-arrow-right-s-line text-gray-400 ml-auto group-hover:text-teal-500 transition-colors"></i>
            </Link>
            <Link
              to="/kullanim-kosullari"
              className="flex items-center space-x-4 p-5 bg-white border border-gray-200 rounded-xl hover:border-rose-300 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-rose-100 rounded-xl flex-shrink-0 group-hover:bg-rose-200 transition-colors">
                <i className="ri-file-text-line text-rose-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-rose-700 transition-colors">{t('kvkk_related_terms')}</h3>
                <p className="text-xs text-gray-500">{t('kvkk_related_terms_desc')}</p>
              </div>
              <i className="ri-arrow-right-s-line text-gray-400 ml-auto group-hover:text-rose-500 transition-colors"></i>
            </Link>
          </div>

          {/* CTA */}
          <div className="p-8 bg-gray-50 rounded-2xl text-center border border-gray-100">
            <div className="w-14 h-14 flex items-center justify-center bg-rose-100 rounded-2xl mx-auto mb-4">
              <i className="ri-question-answer-line text-rose-500 text-2xl"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">{t('kvkk_cta_title')}</h3>
            <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
              {t('kvkk_cta_desc')}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center px-6 py-3 bg-rose-500 text-white rounded-xl font-semibold hover:bg-rose-600 transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              {t('legal_contact_btn')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}